import { useState, useRef, useEffect } from "preact/compat"
import { useAsyncEffect } from "@/utils"
import {
  getContract,
  updateContract,
  analyzeContract,
  uploadNewVersion,
  ContractDocument,
} from "@/api/contract"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { LoadingSpinner } from "@/app/loading"
import { SettingsLayout } from "@/settings/layout"
import { Markdown } from "@/components/markdown"
import { ContractTermsSection } from "@/contracts/contract_terms_section"
import {
  FileText,
  Download,
  Upload,
  RefreshCw,
  Save,
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  Clock,
  Edit,
  X,
  Loader2,
} from "lucide-react"
import { useLocation, useParams } from "wouter-preact"
import { format, parseISO } from "date-fns"

interface ContractDetailPageProps {
  id?: string
}

/** Lucide only merges `className`; put spin on a real DOM node so it actually animates. */
function SpinningLoader({
  className = "h-4 w-4",
  wrapperClassName = "",
}: {
  className?: string
  wrapperClassName?: string
}) {
  return (
    <span class={`inline-flex shrink-0 motion-safe:animate-spin ${wrapperClassName}`} aria-hidden>
      <Loader2 className={className} />
    </span>
  )
}

export function ContractDetailPage({ id: propId }: ContractDetailPageProps) {
  const params = useParams<{ id: string }>()
  const id = propId || params.id
  const [, setLocation] = useLocation()
  const [contract, setContract] = useState<ContractDocument | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingSummary, setEditingSummary] = useState(false)
  const [summaryDraft, setSummaryDraft] = useState("")
  const [savingSummary, setSavingSummary] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [uploadingVersion, setUploadingVersion] = useState(false)
  const [analysisElapsedSec, setAnalysisElapsedSec] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollAttemptsRef = useRef(0)

  function clearPoll() {
    if (pollTimeoutRef.current != null) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
  }

  function startPolling(contractId: string) {
    clearPoll()
    pollAttemptsRef.current = 0
    setAnalyzing(true)

    const maxAttempts = 60 // ~5 minutes at 5s interval

    const poll = async () => {
      if (pollAttemptsRef.current >= maxAttempts) {
        setAnalyzing(false)
        return
      }
      pollAttemptsRef.current++

      try {
        const updated = await getContract(contractId)
        setContract(updated)
        setSummaryDraft(updated.summary || "")

        if (updated.analysis_status === "analyzing") {
          pollTimeoutRef.current = setTimeout(poll, 5000)
        } else {
          setAnalyzing(false)
        }
      } catch {
        setAnalyzing(false)
      }
    }

    pollTimeoutRef.current = setTimeout(poll, 3000)
  }

  async function loadContract() {
    setLoading(true)
    setError(null)
    try {
      const data = await getContract(id)
      setContract(data)
      setSummaryDraft(data.summary || "")
      if (data.analysis_status === "analyzing") {
        startPolling(data.id)
      } else {
        clearPoll()
        setAnalyzing(false)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load contract")
    } finally {
      setLoading(false)
    }
  }

  useAsyncEffect(loadContract, [id])

  useEffect(() => {
    return () => clearPoll()
  }, [])

  const isAnalyzing = analyzing || contract?.analysis_status === "analyzing"

  useEffect(() => {
    if (!isAnalyzing) {
      setAnalysisElapsedSec(0)
      return
    }
    setAnalysisElapsedSec(0)
    const timer = setInterval(() => {
      setAnalysisElapsedSec((sec) => sec + 1)
    }, 1000)
    return () => clearInterval(timer)
  }, [isAnalyzing])

  async function handleSaveSummary() {
    if (!contract) return
    setSavingSummary(true)
    try {
      const updated = await updateContract(contract.id, { summary: summaryDraft })
      setContract(updated)
      setEditingSummary(false)
    } catch (e) {
      console.error("Failed to save summary:", e)
    } finally {
      setSavingSummary(false)
    }
  }

  async function handleReanalyze() {
    if (!contract) return
    setAnalyzing(true)
    try {
      const updated = await analyzeContract(contract.id)
      setContract(updated)
      startPolling(contract.id)
    } catch (e) {
      console.error("Failed to start analysis:", e)
      setAnalyzing(false)
    }
  }

  async function handleUploadNewVersion(e: Event) {
    const input = e.target as HTMLInputElement
    if (!input.files || !input.files[0] || !contract) return

    const file = input.files[0]
    setUploadingVersion(true)
    try {
      const updated = await uploadNewVersion(contract.id, file)
      setContract(updated)
      setSummaryDraft(updated.summary || "")
      startPolling(updated.id)
    } catch (e) {
      console.error("Failed to upload new version:", e)
    } finally {
      setUploadingVersion(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  function handleDownload() {
    if (!contract) return
    // Open presigned URL in new tab
    window.open(`/api/_s3?s3_uri=${encodeURIComponent(contract.s3_uri)}`, "_blank")
  }

  function getStatusBadge(status: string) {
    switch (status) {
      case "completed":
        return (
          <span class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
            <CheckCircle className="h-3 w-3" />
            Analyzed
          </span>
        )
      case "analyzing":
        return (
          <span class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
            <SpinningLoader className="h-3 w-3" />
            Analyzing
          </span>
        )
      case "failed":
        return (
          <span class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
            <AlertCircle className="h-3 w-3" />
            Failed
          </span>
        )
      default:
        return (
          <span class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
            <Clock className="h-3 w-3" />
            Pending
          </span>
        )
    }
  }

  function getKindLabel(kind: string) {
    switch (kind) {
      case "marketing":
        return "Marketing Contract"
      case "freight":
        return "Freight Contract"
      case "purchasing":
        return "Purchasing Contract"
      default:
        return kind
    }
  }

  if (loading) {
    return (
      <SettingsLayout>
        <div class="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      </SettingsLayout>
    )
  }

  if (error || !contract) {
    return (
      <SettingsLayout>
        <div class="text-center py-12">
          <AlertCircle className="h-12 w-12 mx-auto text-red-400 mb-4" />
          <p class="text-gray-500 mb-4">{error || "Contract not found"}</p>
          <Button variant="outline" onClick={() => setLocation("/settings/connections")}>
            Back to Settings
          </Button>
        </div>
      </SettingsLayout>
    )
  }

  return (
    <SettingsLayout>
      <div class="max-w-3xl mx-auto">
        {/* Header */}
        <div class="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation(`/settings/config/${contract.source.toLowerCase()}`)}
            class="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to {contract.source.toUpperCase()} Settings
          </Button>

          <div class="flex items-start justify-between">
            <div>
              <div class="flex items-center gap-3">
                <FileText className="h-8 w-8 text-gray-400" />
                <div>
                  <h1 class="text-2xl font-bold">{contract.name}</h1>
                  <div class="flex items-center gap-2 mt-1">
                    <span class="text-sm text-gray-500">{getKindLabel(contract.kind)}</span>
                    <span class="text-gray-300">|</span>
                    <span class="text-sm text-gray-500">{contract.source.toUpperCase()}</span>
                    {contract.version > 1 && (
                      <>
                        <span class="text-gray-300">|</span>
                        <span class="text-sm text-gray-500">Version {contract.version}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {getStatusBadge(contract.analysis_status)}
          </div>
        </div>

        {/* Actions */}
        <div class="flex gap-2 mb-6">
          <Button variant="outline" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download Original
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingVersion}
          >
            {uploadingVersion ? (
              <SpinningLoader className="h-4 w-4" wrapperClassName="mr-2" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Upload New Version
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleUploadNewVersion}
            class="hidden"
          />
          <Button
            variant="outline"
            onClick={handleReanalyze}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? (
              <SpinningLoader className="h-4 w-4" wrapperClassName="mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Re-analyze
          </Button>
        </div>

        {/* Error Message */}
        {contract.analysis_status === "failed" && contract.analysis_error && (
          <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div class="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div>
                <h3 class="font-medium text-red-800">Analysis Failed</h3>
                <p class="text-sm text-red-600 mt-1">{contract.analysis_error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Summary Section */}
        <div class="bg-white border rounded-lg p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold">Contract Summary</h2>
            {!editingSummary && contract.summary && !isAnalyzing && (
              <Button variant="ghost" size="sm" onClick={() => setEditingSummary(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>

          {isAnalyzing ? (
            <div class="flex flex-col items-center justify-center py-12 text-center gap-3">
              <SpinningLoader className="h-8 w-8 text-blue-500" />
              <div class="space-y-1">
                <p class="text-sm font-medium text-gray-700">Analyzing contract...</p>
                <p class="text-xs text-gray-400">
                  Extracting key terms
                  {analysisElapsedSec >= 3 ? ` · ${analysisElapsedSec}s elapsed` : ""}
                </p>
              </div>
            </div>
          ) : editingSummary ? (
            <div class="space-y-4">
              <Textarea
                value={summaryDraft}
                onChange={(e) => setSummaryDraft((e.target as HTMLTextAreaElement).value)}
                rows={12}
                placeholder="Enter contract summary..."
                class="font-mono text-sm"
              />
              <div class="flex gap-2">
                <Button onClick={handleSaveSummary} disabled={savingSummary}>
                  {savingSummary ? (
                    <SpinningLoader className="h-4 w-4" wrapperClassName="mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingSummary(false)
                    setSummaryDraft(contract.summary || "")
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : contract.summary ? (
            <Markdown content={contract.summary} className="text-sm text-gray-800" />
          ) : (
            <div class="text-center py-8 text-gray-500">
              <p>No summary available.</p>
              <Button variant="outline" class="mt-4" onClick={handleReanalyze}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Analyze Contract
              </Button>
            </div>
          )}
        </div>

        {/* Term extraction can fail after the summary was saved */}
        {contract.analysis_status === "completed" && contract.analysis_error && (
          <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6 text-sm text-amber-800">
            {contract.analysis_error}
          </div>
        )}

        {/* Contract Terms — remounted after analysis so fresh suggestions load */}
        {!isAnalyzing && <ContractTermsSection contract_document_id={contract.id} className="mt-6" />}

        {/* Metadata */}
        <div class="mt-6 text-sm text-gray-500">
          <p>
            Uploaded on {format(parseISO(contract.created_at), "MMMM d, yyyy 'at' h:mm a")}
          </p>
          {contract.updated_at !== contract.created_at && (
            <p>
              Last updated on {format(parseISO(contract.updated_at), "MMMM d, yyyy 'at' h:mm a")}
            </p>
          )}
        </div>
      </div>
    </SettingsLayout>
  )
}
